import { useState, useCallback, useMemo, useEffect } from 'react'
import type { QuizSolveResult, QuizMediaObject, BaseQuizData } from '../../../types/legacyQuiz'

export interface ColoringQuizData extends BaseQuizData {
  type: 'coloring'
  digits: number
  rows?: number
  inverted?: boolean
  coloringOnImage?: QuizMediaObject | null
  coloringOffImage?: QuizMediaObject | null
}

export interface UseColoringQuizOptions {
  quizId: string | number
  quiz: ColoringQuizData
  onSolve?: (quizId: string | number, answers: string[], vars: Record<string, unknown>, elapsedTime: number) => void
  solveResult?: QuizSolveResult | null
  showReallyTrue?: boolean
  isViewSolved?: boolean
  initialAnswers?: string[]
  onAnswerChange?: (answers: string[]) => void
}

export function useColoringQuiz({
  quizId,
  quiz,
  onSolve,
  solveResult,
  showReallyTrue = false,
  isViewSolved = false,
  initialAnswers: initialAnswersProp,
  onAnswerChange,
}: UseColoringQuizOptions) {
  const [startTime] = useState(() => Date.now())
  const [isShowingCorrectAnswers, setIsShowingCorrectAnswers] = useState(false)

  const isSolved = solveResult !== null && solveResult !== undefined
  const digits = quiz.digits || 0
  const rows = quiz.rows || 1
  const inverted = quiz.inverted ?? false
  const shouldShowCorrectAnswers = showReallyTrue || isShowingCorrectAnswers

  const onImageSrc = quiz.coloringOnImage?.signedUrl ?? quiz.coloringOnImage?.url ?? null
  const offImageSrc = quiz.coloringOffImage?.signedUrl ?? quiz.coloringOffImage?.url ?? null
  const hasImages = !!(onImageSrc && offImageSrc)
  const hasInitialAnswers = initialAnswersProp !== undefined

  const [answers, setAnswers] = useState<number[]>(() => {
    if (hasInitialAnswers) {
      const initial = initialAnswersProp[0] ?? ''
      return initial.length > 0 ? [...initial].map(Number) : Array(digits).fill(inverted ? 1 : 0)
    }
    if (isViewSolved && solveResult?.correctAnswers && solveResult.correctAnswers.length > 0) {
      return [...solveResult.correctAnswers[0]].map(Number)
    }
    return Array(digits).fill(inverted ? 1 : 0)
  })

  // Re-sync only when the *content* of incoming answers changes (not just reference).
  // Parents typically wire onAnswerChange → setState → new initialAnswers reference each toggle,
  // which would otherwise create a render loop and cause images to flicker.
  const initialAnswersSignature = initialAnswersProp?.[0] ?? ''
  useEffect(() => {
    if (!initialAnswersProp || initialAnswersProp.length === 0 || initialAnswersSignature.length === 0) return
    const next = [...initialAnswersSignature].map(Number)
    setAnswers(prev => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev
      return next
    })

  }, [initialAnswersSignature])

  useEffect(() => {
    if (hasInitialAnswers) return
    if (!isViewSolved) return
    if (!solveResult?.correctAnswers || solveResult.correctAnswers.length === 0) return
    const next = [...solveResult.correctAnswers[0]].map(Number)
    setAnswers(prev => {
      if (prev.length === next.length && prev.every((v, i) => v === next[i])) return prev
      return next
    })
  }, [hasInitialAnswers, isViewSolved, solveResult?.correctAnswers])

  useEffect(() => {
    onAnswerChange?.([answers.join('')])
  }, [answers, onAnswerChange])

  const correctColorCount = useMemo(() => {
    if (!solveResult?.correctAnswers || solveResult.correctAnswers.length === 0) return null
    const val = parseInt(solveResult.correctAnswers[0], 10)
    return isNaN(val) ? null : val
  }, [solveResult?.correctAnswers])

  const correctAnswerArray = useMemo(() => {
    if (correctColorCount === null) return null
    const arr = Array(digits).fill(0)
    for (let i = 0; i < Math.min(correctColorCount, digits); i++) {
      arr[i] = 1
    }
    return arr
  }, [correctColorCount, digits])

  const itemsPerRow = useMemo(() => Math.ceil(digits / rows), [digits, rows])
  const rowIndices = useMemo(() => {
    const result: number[][] = []
    for (let r = 0; r < rows; r++) {
      const start = r * itemsPerRow
      const end = Math.min(start + itemsPerRow, digits)
      const indices: number[] = []
      for (let i = start; i < end; i++) {
        indices.push(i)
      }
      result.push(indices)
    }
    return result
  }, [rows, itemsPerRow, digits])

  const handleToggle = useCallback((index: number) => {
    if (isSolved || isViewSolved) return
    setAnswers(prev => {
      const next = [...prev]
      next[index] = next[index] === 1 ? 0 : 1
      return next
    })
  }, [isSolved, isViewSolved])

  const handleSubmit = useCallback(() => {
    if (isSolved) return
    const elapsedTime = Date.now() - startTime
    onSolve?.(quizId, [answers.join('')], quiz.vars, elapsedTime)
  }, [isSolved, startTime, onSolve, quizId, answers, quiz.vars])

  const coloredCount = answers.filter(a => a === 1).length

  const getImageSrc = useCallback((index: number): string => {
    if (isSolved && shouldShowCorrectAnswers && correctAnswerArray) {
      return correctAnswerArray[index] === 1 ? (onImageSrc ?? '') : (offImageSrc ?? '')
    }
    return answers[index] === 1 ? (onImageSrc ?? '') : (offImageSrc ?? '')
  }, [isSolved, shouldShowCorrectAnswers, correctAnswerArray, answers, onImageSrc, offImageSrc])

  const getIsColored = useCallback((index: number): boolean => {
    if (isSolved && shouldShowCorrectAnswers && correctAnswerArray) {
      return correctAnswerArray[index] === 1
    }
    return answers[index] === 1
  }, [isSolved, shouldShowCorrectAnswers, correctAnswerArray, answers])

  return {
    answers,
    isSolved,
    shouldShowCorrectAnswers,
    isShowingCorrectAnswers,
    setIsShowingCorrectAnswers,
    digits,
    rows,
    inverted,
    hasImages,
    onImageSrc,
    offImageSrc,
    correctColorCount,
    correctAnswerArray,
    itemsPerRow,
    rowIndices,
    coloredCount,
    handleToggle,
    handleSubmit,
    getImageSrc,
    getIsColored,
    quiz,
    quizId,
    solveResult,
    isViewSolved,
  }
}
